/** Small helpers for the admin edit forms (PATCH sends only what changed — RULES §WRITE-2). */
import { t } from '@/lib/i18n';
import type { ComboOption } from '@/ui';
import { TIMEZONES } from '../labels';

/** The fields of `next` that differ from `before` (strings compared trimmed). */
export function changed<T extends Record<string, unknown>>(before: T, next: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k of Object.keys(next) as (keyof T)[]) {
    const a = before[k];
    const b = next[k];
    const same = typeof a === 'string' && typeof b === 'string' ? a.trim() === b.trim() : JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    if (!same) out[k] = typeof b === 'string' ? (b.trim() as T[keyof T]) : b;
  }
  return out;
}

export const isEmpty = (o: object) => Object.keys(o).length === 0;

/** Every IANA zone the browser knows, common Benelux/EU zones first. */
export function timeZoneOptions(current?: string | null): ComboOption[] {
  let all: string[];
  try {
    all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    all = [];
  }
  const first = [...TIMEZONES, ...(current && !TIMEZONES.includes(current) ? [current] : [])];
  const rest = all.filter((z) => !first.includes(z));
  const common = t('Common');
  const other = t('All time zones');
  return [...first.map((z) => ({ value: z, label: z, group: common })), ...rest.map((z) => ({ value: z, label: z, group: other }))];
}

export const HEX = /^#[0-9A-Fa-f]{6}$/;
export const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** A local date input value (YYYY-MM-DD) → the end of that day in the browser's zone, as ISO. */
export function endOfDayIso(day: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const d = new Date(`${day}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** An ISO instant → YYYY-MM-DD for a date input. */
export function toDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Milliseconds from now until an instant (negative = past). Relative displays only. */
export function msUntil(iso: string): number {
  return new Date(iso).getTime() - Date.now();
}

/** Tomorrow as YYYY-MM-DD (the earliest expiry a date input should offer). */
export function tomorrow(): string {
  return toDay(new Date(Date.now() + 86_400_000).toISOString());
}
